import { gql } from "@apollo/client";

// ──────────────────────────────────────────────────────────────────────────
// Shared scalar types
// ──────────────────────────────────────────────────────────────────────────

export interface AcDataScope {
  requireIpaMatch: boolean | null;
  allowedLineOfBusiness: string[] | null;
}

// AcPersonaModule — used inside currentUser.currentPersona (no isActive field)
export interface AcPersonaModule {
  moduleId: string;
  sortOrder: number;
  actions: string[] | null;
  dataScope: AcDataScope | null;
}

// AcAdminPersonaModule — used in listPersonas (has isActive)
export interface AcAdminPersonaModule {
  moduleId: string;
  sortOrder: number;
  actions: string[] | null;
  dataScope: AcDataScope | null;
  isActive: boolean;
}

export interface AcAdminPersona {
  code: string;
  displayName: string;
  // `modules` is this persona's OWN rows only (unchanged meaning — pre-dates
  // Persona Groups). `groupCode` (null = ungrouped) and `effectiveModules`
  // (the server's already-merged own+group view) are additive.
  modules: AcAdminPersonaModule[];
  groupCode: string | null;
  effectiveModules: AcAdminPersonaModule[];
}

// AcPersonaSummary — used in currentUser.personas[] (no modules)
export interface AcPersonaSummary {
  code: string;
  displayName: string;
}

// AcCurrentPersona — used in currentUser.currentPersona (has modules, no isActive)
export interface AcCurrentPersona {
  code: string;
  displayName: string;
  modules: AcPersonaModule[];
}

export interface AcRole {
  code: string;
  displayName: string;
}

export interface AcPermissionMeta {
  ipas: string[] | null;
  healthPlans: string[] | null;
  lineOfBusiness: string[] | null;
}

export interface AcPermission {
  value: string;
  permissionType: string;
  permissionMeta: AcPermissionMeta | null;
}

export interface AcCurrentUser {
  id: string;
  email: string;
  displayName: string | null;
  role: AcRole | null;
  // personas[] is AcPersonaSummary — NO modules field on this list
  personas: AcPersonaSummary[];
  // currentPersona has modules (AcPersonaModule), dataScope is an object
  currentPersona: AcCurrentPersona | null;
  permissions: AcPermission[];
}

// AcUserSummary — returned by listUsers
export interface AcUserSummary {
  id: string;
  email: string;
  displayName: string | null;
  status: string;
  role: AcRole | null;
  personas: AcPersonaSummary[];
}

// ──────────────────────────────────────────────────────────────────────────
// Reusable fragments
// ──────────────────────────────────────────────────────────────────────────

// dataScope subfields — AcDataScope object (NOT a scalar string)
const DATA_SCOPE_FIELDS = `
  dataScope {
    requireIpaMatch
    allowedLineOfBusiness
  }
`;

// currentUser full payload — reused in mutation return selections
const CURRENT_USER_FIELDS = `
  id
  email
  displayName
  role {
    code
    displayName
  }
  personas {
    code
    displayName
  }
  currentPersona {
    code
    displayName
    modules {
      moduleId
      sortOrder
      actions
      ${DATA_SCOPE_FIELDS}
    }
  }
  permissions {
    value
    permissionType
    permissionMeta {
      ipas
      healthPlans
      lineOfBusiness
    }
  }
`;

// ──────────────────────────────────────────────────────────────────────────
// Query: currentUser
// ──────────────────────────────────────────────────────────────────────────

export const CURRENT_USER_QUERY = gql`
  query AcCurrentUser {
    currentUser {
      ${CURRENT_USER_FIELDS}
    }
  }
`;

export interface CurrentUserResult {
  currentUser: AcCurrentUser | null;
}

// ──────────────────────────────────────────────────────────────────────────
// Query: listUsers  (AC-ADM-1)
// ──────────────────────────────────────────────────────────────────────────

export const LIST_USERS_QUERY = gql`
  query AcListUsers($input: AcListUsersInput) {
    listUsers(input: $input) {
      items {
        id
        email
        displayName
        status
        role {
          code
          displayName
        }
        personas {
          code
          displayName
        }
      }
      pageInformation {
        totalRecords
      }
    }
  }
`;

// Mirrors the BE AcListUsersInput (care-management-gql AcAdminResolver.listUsers).
// page is 0-indexed; pageSize is capped at 200 (MAX_USERS_PAGE_SIZE) server-side.
export interface ListUsersInput {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  roleCode?: string;
  personaCode?: string;
}

export interface ListUsersResult {
  listUsers: {
    items: AcUserSummary[];
    pageInformation: { totalRecords: number | null } | null;
  };
}

// ──────────────────────────────────────────────────────────────────────────
// Query: listRoles  (AC-ADM-1 — role filter catalog for the Users page)
// ──────────────────────────────────────────────────────────────────────────

export const LIST_ROLES_QUERY = gql`
  query AcListRoles {
    listRoles {
      code
      displayName
    }
  }
`;

export interface ListRolesResult {
  listRoles: { code: string; displayName: string }[];
}

// ──────────────────────────────────────────────────────────────────────────
// Query: listPersonas  (AC-ADM-3)
// AcAdminPersona.modules is AcAdminPersonaModule (has isActive)
// ──────────────────────────────────────────────────────────────────────────

export const LIST_PERSONAS_QUERY = gql`
  query AcListPersonas {
    listPersonas {
      code
      displayName
      groupCode
      modules {
        moduleId
        sortOrder
        actions
        isActive
        ${DATA_SCOPE_FIELDS}
      }
      effectiveModules {
        moduleId
        sortOrder
        actions
        isActive
        ${DATA_SCOPE_FIELDS}
      }
    }
  }
`;

export interface ListPersonasResult {
  listPersonas: AcAdminPersona[];
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: createUser  (AC-ADM-1)
// Returns AcCurrentUser (full payload)
// ──────────────────────────────────────────────────────────────────────────

export const CREATE_USER_MUTATION = gql`
  mutation AcCreateUser(
    $email: String!
    $displayName: String
    $roleCodes: [String!]
    $personaCodes: [String!]
  ) {
    createUser(
      email: $email
      displayName: $displayName
      roleCodes: $roleCodes
      personaCodes: $personaCodes
    ) {
      ${CURRENT_USER_FIELDS}
    }
  }
`;

export interface CreateUserVars {
  email: string;
  displayName?: string;
  roleCodes?: string[];
  personaCodes?: string[];
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: updateUser  (AC-ADM-1)
// Returns AcCurrentUser (full payload)
// ──────────────────────────────────────────────────────────────────────────

export const UPDATE_USER_MUTATION = gql`
  mutation AcUpdateUser($userId: String!, $displayName: String, $status: String) {
    updateUser(userId: $userId, displayName: $displayName, status: $status) {
      id
      email
      displayName
    }
  }
`;

export interface UpdateUserVars {
  userId: string;
  displayName?: string;
  status?: string;
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: suspendUser  (AC-ADM-1)
// Returns AcCurrentUser (full payload — we just need id/email/status)
// ──────────────────────────────────────────────────────────────────────────

export const SUSPEND_USER_MUTATION = gql`
  mutation AcSuspendUser($userId: String!) {
    suspendUser(userId: $userId) {
      id
      email
    }
  }
`;

export interface SuspendUserVars {
  userId: string;
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: assignPersonas  (AC-ADM-3)
// Returns AcCurrentUser
// ──────────────────────────────────────────────────────────────────────────

export const ASSIGN_PERSONAS_MUTATION = gql`
  mutation AcAssignPersonas($userId: String!, $personaCodes: [String!]!) {
    assignPersonas(userId: $userId, personaCodes: $personaCodes) {
      id
      personas {
        code
        displayName
      }
    }
  }
`;

export interface AssignPersonasVars {
  userId: string;
  personaCodes: string[];
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: assignRole  (admin edit-role)
// Sets an existing user's role. The backend REPLACES the user's role set with
// the single supplied code and validates it against member_360.roles; it is
// admin-gated server-side (assertAdmin), so the FE guard is never the only line
// of defense. Returns the user's id + (new highest-precedence) role.
// ──────────────────────────────────────────────────────────────────────────

export const ASSIGN_ROLE_MUTATION = gql`
  mutation AcAssignRole($userId: String!, $roleCode: String!) {
    assignRole(userId: $userId, roleCode: $roleCode) {
      id
      role {
        code
        displayName
      }
    }
  }
`;

export interface AssignRoleVars {
  userId: string;
  roleCode: string;
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: setCurrentPersona  (Screen 3)
// Returns AcCurrentUser
// ──────────────────────────────────────────────────────────────────────────

export const SET_CURRENT_PERSONA_MUTATION = gql`
  mutation AcSetCurrentPersona($personaCode: String!) {
    setCurrentPersona(personaCode: $personaCode) {
      ${CURRENT_USER_FIELDS}
    }
  }
`;

export interface SetCurrentPersonaVars {
  personaCode: string;
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: createPersona  (AC-ADM-3)
// Creates a new persona row, optionally attaching modules atomically-ish in the
// same call. Returns the full AcAdminPersona (code, displayName, modules[] with
// isActive) — same shape listPersonas returns — so the FE can insert it into the
// list without a refetch round-trip.
//
// `modules` uses the AcPersonaModuleInput input type. dataScope inside each input
// is a JSON string on the wire (same serialization upsertPersonaModule uses).
// Backend note: persona insert + module attach are SEQUENTIAL, not one txn — a
// module failure leaves the persona created but partially configured (retryable
// via the per-module upsert). Duplicate code → "Persona code 'X' already exists".
// ──────────────────────────────────────────────────────────────────────────

export interface AcPersonaModuleInput {
  moduleId: string;
  sortOrder: number;
  actions?: string[];
  dataScope?: string; // JSON string
}

export const CREATE_PERSONA_MUTATION = gql`
  mutation AcCreatePersona(
    $code: String!
    $displayName: String!
    $modules: [AcPersonaModuleInput!]
  ) {
    createPersona(code: $code, displayName: $displayName, modules: $modules) {
      code
      displayName
      groupCode
      modules {
        moduleId
        sortOrder
        actions
        isActive
        ${DATA_SCOPE_FIELDS}
      }
      effectiveModules {
        moduleId
        sortOrder
        actions
        isActive
        ${DATA_SCOPE_FIELDS}
      }
    }
  }
`;

export interface CreatePersonaVars {
  code: string;
  displayName: string;
  modules?: AcPersonaModuleInput[];
}

export interface CreatePersonaResult {
  createPersona: AcAdminPersona;
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: upsertPersonaModule  (AC-ADM-3)
// Returns AcPersonaModule (NOT AcAdminPersona).
// IMPORTANT: dataScope arg is a JSON string (serialized), not an object.
// The resolver stores it as jsonb; pass stringified JSON or undefined.
// ──────────────────────────────────────────────────────────────────────────

export const UPSERT_PERSONA_MODULE_MUTATION = gql`
  mutation AcUpsertPersonaModule(
    $personaCode: String!
    $moduleId: String!
    $sortOrder: Int!
    $actions: [String!]
    $dataScope: String
    $isActive: Boolean
  ) {
    upsertPersonaModule(
      personaCode: $personaCode
      moduleId: $moduleId
      sortOrder: $sortOrder
      actions: $actions
      dataScope: $dataScope
      isActive: $isActive
    ) {
      moduleId
      sortOrder
      actions
      ${DATA_SCOPE_FIELDS}
    }
  }
`;

export interface UpsertPersonaModuleVars {
  personaCode: string;
  moduleId: string;
  sortOrder: number;
  actions?: string[];
  dataScope?: string; // JSON string
  isActive?: boolean;
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: deactivatePersonaModule  (AC-ADM-3)
// care-management-gql returns a scalar Boolean (true on success) — NOT an
// AcPersonaModule object. Selecting object subfields would 400 against the
// care-mgmt schema, so this mutation has no selection set.
// ──────────────────────────────────────────────────────────────────────────

export const DEACTIVATE_PERSONA_MODULE_MUTATION = gql`
  mutation AcDeactivatePersonaModule($personaCode: String!, $moduleId: String!) {
    deactivatePersonaModule(personaCode: $personaCode, moduleId: $moduleId)
  }
`;

export interface DeactivatePersonaModuleVars {
  personaCode: string;
  moduleId: string;
}

export interface DeactivatePersonaModuleResult {
  deactivatePersonaModule: boolean;
}

// ──────────────────────────────────────────────────────────────────────────
// Mutation: createUsers  (ACT-1293 — CSV bulk import)
//
// Input: CreateUserInput { email, displayName, roleCodes, personaCodes }
// Result per row: CreateUsersResult { email, success, error, user }
//
// Partial success: some rows can succeed while others fail.
// Backend max: 500 rows per call — caller must chunk if CSV > 500 rows.
// ──────────────────────────────────────────────────────────────────────────

export interface CreateUserInput {
  email: string;
  displayName?: string;
  roleCodes?: string[];
  personaCodes?: string[];
}

// Narrow user shape — the results table only needs id/email/displayName;
// requesting the full CURRENT_USER_FIELDS for up to 500 users is wasteful.
export interface CreateUsersUserSummary {
  id: string;
  email: string;
  displayName: string | null;
}

export interface CreateUsersRowResult {
  email: string;
  success: boolean;
  error: string | null;
  user: CreateUsersUserSummary | null;
}

export interface CreateUsersVars {
  users: CreateUserInput[];
}

export interface CreateUsersResult {
  createUsers: CreateUsersRowResult[];
}

export const CREATE_USERS_MUTATION = gql`
  mutation AcCreateUsers($users: [CreateUserInput!]!) {
    createUsers(users: $users) {
      email
      success
      error
      user {
        id
        email
        displayName
      }
    }
  }
`;


// ──────────────────────────────────────────────────────────────────────────
// Query: personaModuleOptions  (AC-ADM-3 — dropdown catalogs)
// Deterministic option lists for the Add/Edit Module modal: distinct-in-use ∪
// canonical defaults (moduleIds, lineOfBusiness), distinct-in-use actions, and all
// personas. Admin-gated on the backend.
// ──────────────────────────────────────────────────────────────────────────

export interface PersonaModuleOptions {
  moduleIds: string[];
  actions: string[];
  lineOfBusiness: string[];
  personas: AcPersonaSummary[];
}

export const PERSONA_MODULE_OPTIONS_QUERY = gql`
  query AcPersonaModuleOptions {
    personaModuleOptions {
      moduleIds
      actions
      lineOfBusiness
      personas {
        code
        displayName
      }
    }
  }
`;

export interface PersonaModuleOptionsResult {
  personaModuleOptions: PersonaModuleOptions;
}

// ──────────────────────────────────────────────────────────────────────────
// Persona Groups (ACT-persona-groups)
//
// A group holds module permissions every member persona inherits (floor
// semantics — see personaGroupLogic.ts's resolveEffectiveModules). A group's
// own `modules` here is the SAME AcAdminPersonaModule shape a persona uses
// (moduleId/sortOrder/actions/dataScope/isActive) — the two are structurally
// identical, just attached to a different kind of row.
// ──────────────────────────────────────────────────────────────────────────

export interface AcPersonaGroup {
  code: string;
  displayName: string;
  isActive: boolean;
  modules: AcAdminPersonaModule[];
  personaCodes: string[];
}

export const LIST_PERSONA_GROUPS_QUERY = gql`
  query AcListPersonaGroups {
    listPersonaGroups {
      code
      displayName
      isActive
      modules {
        moduleId
        sortOrder
        actions
        isActive
        ${DATA_SCOPE_FIELDS}
      }
      personaCodes
    }
  }
`;

export interface ListPersonaGroupsResult {
  listPersonaGroups: AcPersonaGroup[];
}

// Mutation: createPersonaGroup
export const CREATE_PERSONA_GROUP_MUTATION = gql`
  mutation AcCreatePersonaGroup(
    $code: String!
    $displayName: String!
    $modules: [AcPersonaModuleInput!]
  ) {
    createPersonaGroup(code: $code, displayName: $displayName, modules: $modules) {
      code
      displayName
      isActive
      modules {
        moduleId
        sortOrder
        actions
        isActive
        ${DATA_SCOPE_FIELDS}
      }
      personaCodes
    }
  }
`;

export interface CreatePersonaGroupVars {
  code: string;
  displayName: string;
  modules?: AcPersonaModuleInput[];
}

export interface CreatePersonaGroupResult {
  createPersonaGroup: AcPersonaGroup;
}

// Mutation: upsertPersonaGroupModule — same wire contract as
// upsertPersonaModule (dataScope is a JSON string; COALESCE semantics on
// omitted actions/dataScope — always send the exact target set).
export const UPSERT_PERSONA_GROUP_MODULE_MUTATION = gql`
  mutation AcUpsertPersonaGroupModule(
    $groupCode: String!
    $moduleId: String!
    $sortOrder: Int!
    $actions: [String!]
    $dataScope: String
    $isActive: Boolean
  ) {
    upsertPersonaGroupModule(
      groupCode: $groupCode
      moduleId: $moduleId
      sortOrder: $sortOrder
      actions: $actions
      dataScope: $dataScope
      isActive: $isActive
    )
  }
`;

export interface UpsertPersonaGroupModuleVars {
  groupCode: string;
  moduleId: string;
  sortOrder: number;
  actions?: string[];
  dataScope?: string;
  isActive?: boolean;
}

export interface UpsertPersonaGroupModuleResult {
  upsertPersonaGroupModule: boolean;
}

// Mutation: deactivatePersonaGroupModule
export const DEACTIVATE_PERSONA_GROUP_MODULE_MUTATION = gql`
  mutation AcDeactivatePersonaGroupModule($groupCode: String!, $moduleId: String!) {
    deactivatePersonaGroupModule(groupCode: $groupCode, moduleId: $moduleId)
  }
`;

export interface DeactivatePersonaGroupModuleVars {
  groupCode: string;
  moduleId: string;
}

export interface DeactivatePersonaGroupModuleResult {
  deactivatePersonaGroupModule: boolean;
}

// Mutation: assignPersonaToGroup — groupCode: null ungroups the persona.
export const ASSIGN_PERSONA_TO_GROUP_MUTATION = gql`
  mutation AcAssignPersonaToGroup($personaCode: String!, $groupCode: String) {
    assignPersonaToGroup(personaCode: $personaCode, groupCode: $groupCode)
  }
`;

export interface AssignPersonaToGroupVars {
  personaCode: string;
  groupCode: string | null;
}

export interface AssignPersonaToGroupResult {
  assignPersonaToGroup: boolean;
}

// Mutation: deletePersonaGroup
export type AcDeleteGroupMemberAction = "UNGROUP" | "DELETE_PERSONAS";

export const DELETE_PERSONA_GROUP_MUTATION = gql`
  mutation AcDeletePersonaGroup($groupCode: String!, $onMembers: AcDeleteGroupMemberAction!) {
    deletePersonaGroup(groupCode: $groupCode, onMembers: $onMembers)
  }
`;

export interface DeletePersonaGroupVars {
  groupCode: string;
  onMembers: AcDeleteGroupMemberAction;
}

export interface DeletePersonaGroupResult {
  deletePersonaGroup: boolean;
}

// ──────────────────────────────────────────────────────────────────────────
// Feature Flags  (ACT-1235 — now REAL)
// ──────────────────────────────────────────────────────────────────────────

export interface AcFeatureFlag {
  key: string;
  displayName: string;
  description: string | null;
  enabled: boolean;
  updatedAt: string;
  updatedBy: string | null;
}

export const LIST_FEATURE_FLAGS_QUERY = gql`
  query AcListFeatureFlags {
    listFeatureFlags {
      key
      displayName
      description
      enabled
      updatedAt
      updatedBy
    }
  }
`;

export interface ListFeatureFlagsResult {
  listFeatureFlags: AcFeatureFlag[];
}

export const SET_FEATURE_FLAG_MUTATION = gql`
  mutation AcSetFeatureFlag($key: String!, $enabled: Boolean!) {
    setFeatureFlag(key: $key, enabled: $enabled) {
      key
      displayName
      description
      enabled
      updatedAt
      updatedBy
    }
  }
`;

export interface SetFeatureFlagVars {
  key: string;
  enabled: boolean;
}

// ──────────────────────────────────────────────────────────────────────────
// AI Templates  (instruction templates for the clinical-summary service)
//
// Immutable versioning: each edit publishes a NEW ai_template_version; the
// template's activeVersion pointer is what the summary service reads from
// Postgres. Templates are soft-disabled via isActive, never hard-deleted.
//
// GraphQL operations (backend resolvers live in care-management-gql — NOT this
// repo — so these ops error until the backend adds them):
//   Query:    AcAiTemplates            → aiTemplates[]        (list + activeVersion.version)
//   Query:    AcAiTemplate($key)       → aiTemplate           (full, incl activeVersion)
//   Query:    AcAiTemplateVersions     → aiTemplateVersions[] (version history)
//   Mutation: AcCreateAiTemplate       → creates template + version 1 + sets active
//   Mutation: AcPublishAiTemplateVersion → inserts a new immutable version
//   Mutation: AcSetActiveAiTemplateVersion → repoints activeVersion
//   Mutation: AcSetAiTemplateActive    → soft enable/disable the whole template
// ──────────────────────────────────────────────────────────────────────────

export interface AiTemplateVersion {
  id: string;
  version: number;
  instructions: string;
  model: string | null;
  notes: string | null;
  createdAt: string;
  createdBy: string | null;
}

// AiTemplateListItem — returned by aiTemplates[] (activeVersion is the version
// number only; the full instruction body is fetched on demand via aiTemplate).
export interface AiTemplateListItem {
  id: string;
  key: string;
  name: string;
  description: string | null;
  isActive: boolean;
  /** See AiTemplate.usesPrevisitData — the DATA pipeline this template declares. */
  usesPrevisitData: boolean;
  activeVersion: { version: number } | null;
  createdAt: string;
  updatedAt: string;
}

// AiTemplate — returned by aiTemplate(key) and the mutations. activeVersion is
// the full version payload (instructions etc.).
export interface AiTemplate {
  id: string;
  key: string;
  name: string;
  description: string | null;
  isActive: boolean;
  /**
   * Which DATA the patient-summary service builds this template from:
   * true = open care gaps + a narrative scoped to the last visit (the pre-round
   * briefing); false = full history, no gaps. Backed by
   * member_360.ai_template.uses_previsit_data.
   */
  usesPrevisitData: boolean;
  activeVersion: AiTemplateVersion | null;
  createdAt: string;
  updatedAt: string;
}

// Full AiTemplate selection (incl full activeVersion) — reused by the single-
// template query and every mutation return so the FE gets a consistent shape.
const AI_TEMPLATE_FIELDS = `
  id
  key
  name
  description
  isActive
  usesPrevisitData
  activeVersion {
    id
    version
    instructions
    model
    notes
    createdAt
    createdBy
  }
  createdAt
  updatedAt
`;

export const LIST_AI_TEMPLATES_QUERY = gql`
  query AcAiTemplates {
    aiTemplates {
      id
      key
      name
      description
      isActive
      usesPrevisitData
      activeVersion {
        version
      }
      createdAt
      updatedAt
    }
  }
`;

export interface ListAiTemplatesResult {
  aiTemplates: AiTemplateListItem[];
}

// ── Persona ↔ template links (ACT-1295) ────────────────────────────────────
// Which AI template a persona uses for a given purpose (e.g. 'patient-summary').
export interface PersonaTemplate {
  personaCode: string;
  personaName: string;
  purpose: string;
  templateId: string;
  templateKey: string;
  templateName: string;
  isActive: boolean;
}

export const PERSONA_TEMPLATES_QUERY = gql`
  query AcPersonaTemplates {
    personaTemplates {
      personaCode
      personaName
      purpose
      templateId
      templateKey
      templateName
      isActive
    }
  }
`;

export interface PersonaTemplatesResult {
  personaTemplates: PersonaTemplate[];
}

export const UPSERT_PERSONA_TEMPLATE_MUTATION = gql`
  mutation AcUpsertPersonaTemplate($input: UpsertPersonaTemplateInput!) {
    upsertPersonaTemplate(input: $input)
  }
`;

export interface UpsertPersonaTemplateVars {
  input: { personaCode: string; templateId: string; purpose: string };
}

export const AI_TEMPLATE_QUERY = gql`
  query AcAiTemplate($key: String!) {
    aiTemplate(key: $key) {
      ${AI_TEMPLATE_FIELDS}
    }
  }
`;

export interface AiTemplateVars {
  key: string;
}

export interface AiTemplateResult {
  aiTemplate: AiTemplate | null;
}

export const AI_TEMPLATE_VERSIONS_QUERY = gql`
  query AcAiTemplateVersions($templateId: String!) {
    aiTemplateVersions(templateId: $templateId) {
      id
      version
      instructions
      model
      notes
      createdAt
      createdBy
    }
  }
`;

export interface AiTemplateVersionsVars {
  templateId: string;
}

export interface AiTemplateVersionsResult {
  aiTemplateVersions: AiTemplateVersion[];
}

// createAiTemplate — creates the template row, its version 1, and points
// activeVersion at it, atomically on the backend. Returns the full AiTemplate.
export const CREATE_AI_TEMPLATE_MUTATION = gql`
  mutation AcCreateAiTemplate(
    $key: String!
    $name: String!
    $description: String
    $instructions: String!
  ) {
    createAiTemplate(
      key: $key
      name: $name
      description: $description
      instructions: $instructions
    ) {
      ${AI_TEMPLATE_FIELDS}
    }
  }
`;

export interface CreateAiTemplateVars {
  key: string;
  name: string;
  description?: string;
  instructions: string;
}

export interface CreateAiTemplateResult {
  createAiTemplate: AiTemplate;
}

// publishAiTemplateVersion — inserts a new immutable version (auto-incremented
// version number on the backend). setActive=true repoints activeVersion at it.
// Returns the newly created AiTemplateVersion.
export const PUBLISH_AI_TEMPLATE_VERSION_MUTATION = gql`
  mutation AcPublishAiTemplateVersion(
    $templateId: String!
    $instructions: String!
    $model: String
    $notes: String
    $setActive: Boolean
  ) {
    publishAiTemplateVersion(
      templateId: $templateId
      instructions: $instructions
      model: $model
      notes: $notes
      setActive: $setActive
    ) {
      id
      version
      instructions
      model
      notes
      createdAt
      createdBy
    }
  }
`;

export interface PublishAiTemplateVersionVars {
  templateId: string;
  instructions: string;
  model?: string;
  notes?: string;
  setActive?: boolean;
}

export interface PublishAiTemplateVersionResult {
  publishAiTemplateVersion: AiTemplateVersion;
}

// setActiveAiTemplateVersion — repoint the template's activeVersion at an
// existing version (rollback / promote). Returns the full AiTemplate.
export const SET_ACTIVE_AI_TEMPLATE_VERSION_MUTATION = gql`
  mutation AcSetActiveAiTemplateVersion($templateId: String!, $versionId: String!) {
    setActiveAiTemplateVersion(templateId: $templateId, versionId: $versionId) {
      ${AI_TEMPLATE_FIELDS}
    }
  }
`;

export interface SetActiveAiTemplateVersionVars {
  templateId: string;
  versionId: string;
}

export interface SetActiveAiTemplateVersionResult {
  setActiveAiTemplateVersion: AiTemplate;
}

// setAiTemplateActive — soft enable/disable the whole template (is_active).
// Returns the full AiTemplate.
export const SET_AI_TEMPLATE_ACTIVE_MUTATION = gql`
  mutation AcSetAiTemplateActive($templateId: String!, $isActive: Boolean!) {
    setAiTemplateActive(templateId: $templateId, isActive: $isActive) {
      ${AI_TEMPLATE_FIELDS}
    }
  }
`;

export interface SetAiTemplateActiveVars {
  templateId: string;
  isActive: boolean;
}

export interface SetAiTemplateActiveResult {
  setAiTemplateActive: AiTemplate;
}

// setAiTemplatePrevisitData — declare which DATA the patient-summary service
// builds this template from. true = open care gaps + a narrative scoped to the
// last visit (the pre-round briefing); false = full history, no gaps.
//
// This is the ONLY thing that selects that pipeline. It used to be chosen by the
// CALLING SURFACE (a `context: "pre_visit"` request flag) as well as by a
// hard-coded template-key allowlist in care-management-gql, which meant the
// Patient Clinical Summary template could be handed pre-round data blocks its
// prompt has no slot for. Do not re-introduce a caller-side switch.
//
// Publishes NO new version: the flag describes the template's purpose, not one
// revision of its wording. Cached summaries invalidate on their own, because the
// resolved strategy is part of the summary cache key.
export const SET_AI_TEMPLATE_PREVISIT_DATA_MUTATION = gql`
  mutation AcSetAiTemplatePrevisitData(
    $templateId: String!
    $usesPrevisitData: Boolean!
  ) {
    setAiTemplatePrevisitData(
      templateId: $templateId
      usesPrevisitData: $usesPrevisitData
    ) {
      ${AI_TEMPLATE_FIELDS}
    }
  }
`;

export interface SetAiTemplatePrevisitDataVars {
  templateId: string;
  usesPrevisitData: boolean;
}

export interface SetAiTemplatePrevisitDataResult {
  setAiTemplatePrevisitData: AiTemplate;
}

// ──────────────────────────────────────────────────────────────────────────
// AI Summary settings + cache  (ACT-1295 — the "AI Summary" admin page)
//
// patientSummarySettings gates which data sections feed the connect-sourced
// clinical summary. The four booleans map to member_360.patient_summary_settings
// (the is_current row). clearPatientSummaryCache purges every cached summary in
// Redis (returns the number of entries removed).
//
// Backend resolvers live in care-management-gql (NOT this repo):
//   Query:    AcPatientSummarySettings      → patientSummarySettings
//   Mutation: AcUpdatePatientSummarySettings → updatePatientSummarySettings
//   Mutation: AcClearPatientSummaryCache     → clearPatientSummaryCache (Int)
// ──────────────────────────────────────────────────────────────────────────

export interface PatientSummarySettings {
  claimsEnabled: boolean;
  casesEnabled: boolean;
  labsEnabled: boolean;
  authsEnabled: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

const PATIENT_SUMMARY_SETTINGS_FIELDS = `
  claimsEnabled
  casesEnabled
  labsEnabled
  authsEnabled
  updatedAt
  updatedBy
`;

export const PATIENT_SUMMARY_SETTINGS_QUERY = gql`
  query AcPatientSummarySettings {
    patientSummarySettings {
      ${PATIENT_SUMMARY_SETTINGS_FIELDS}
    }
  }
`;

export interface PatientSummarySettingsResult {
  patientSummarySettings: PatientSummarySettings;
}

export const UPDATE_PATIENT_SUMMARY_SETTINGS_MUTATION = gql`
  mutation AcUpdatePatientSummarySettings(
    $input: UpdatePatientSummarySettingsInput!
  ) {
    updatePatientSummarySettings(input: $input) {
      ${PATIENT_SUMMARY_SETTINGS_FIELDS}
    }
  }
`;

export interface UpdatePatientSummarySettingsVars {
  input: {
    claimsEnabled: boolean;
    casesEnabled: boolean;
    labsEnabled: boolean;
    authsEnabled: boolean;
  };
}

export interface UpdatePatientSummarySettingsResult {
  updatePatientSummarySettings: PatientSummarySettings;
}

export const CLEAR_PATIENT_SUMMARY_CACHE_MUTATION = gql`
  mutation AcClearPatientSummaryCache {
    clearPatientSummaryCache
  }
`;

export interface ClearPatientSummaryCacheResult {
  clearPatientSummaryCache: number;
}
// ──────────────────────────────────────────────────────────────────────────
// Usage Stats  (AC-ADM-4 / ACT-1237)
// Queries care-management-gql UsageResolver (App Insights KQL proxy).
// timestamps come back as String (ISO 8601) — parse with new Date() where needed.
// activeUsers and eventCount are Int.
// ──────────────────────────────────────────────────────────────────────────

export type UsageGroupBy = "PERSONA" | "CLINIC" | "USER" | "TAB";

export interface UsageStatsRow {
  /** ISO 8601 string — first day of the week bin */
  weekStart: string;
  /** Breakdown dimension name: "persona" or "clinic" */
  dimension: string;
  /** The persona code or IPA code value for this row */
  dimensionValue: string;
  /** Distinct authenticated users in this week × dimension bucket */
  activeUsers: number;
  /** Total event count (sum(itemCount) to undo sampling) in this bucket */
  eventCount: number;
}

export interface UsageStatsResult {
  usageStats: {
    rows: UsageStatsRow[];
    totalActiveUsers: number;
    totalEvents: number;
    /**
     * False when the backend has no App Insights connection string configured
     * for the requested env — the dashboard should render an informational
     * notice instead of empty charts.
     */
    isConfigured: boolean;
  };
}

export interface UsageStatsInput {
  startDate?: string;
  endDate?: string;
  groupBy: UsageGroupBy;
  eventName?: string;
  /** Filter results to a specific deployment environment (e.g. "dev", "qa", "uat", "prod"). */
  env?: string;
  /** Filter results to a specific staff user by email address. */
  userEmail?: string;
}

export interface UsageStatsVars {
  input: UsageStatsInput;
}

export const USAGE_STATS_QUERY = gql`
  query UsageStats($input: UsageStatsInput!) {
    usageStats(input: $input) {
      rows {
        weekStart
        dimension
        dimensionValue
        activeUsers
        eventCount
      }
      totalActiveUsers
      totalEvents
      isConfigured
    }
  }
`;

// UsageStatsInput includes: startDate, endDate, groupBy, eventName, env, userEmail

// ──────────────────────────────────────────────────────────────────────────
// Visit notes (auto-generated visit notes)
//
// Which chart sources feed the prompt for each note type, plus the model
// aliases the server will actually honor. Backend resolvers live in
// care-management-gql (VisitNoteSettingsResolver) — NOT this repo — so these
// ops error until that backend ships.
//
//   Query:    AcVisitNoteSettings     → visitNoteSettings[]  (one row per type)
//   Mutation: AcSetVisitNoteSettings  → setVisitNoteSettings (full row upsert)
//   Query:    AcAiModelAliases        → aiModelAliases (String[])
// ──────────────────────────────────────────────────────────────────────────

export type VisitNoteType = "SOAP" | "SIMPLE" | "EM" | "MEAT";

export interface VisitNoteSettings {
  noteType: VisitNoteType;
  isProblemsEnabled: boolean;
  isMedicationsEnabled: boolean;
  isAllergiesEnabled: boolean;
  isVitalsEnabled: boolean;
  isCareGapsEnabled: boolean;
  isPriorNotesEnabled: boolean;
  updatedBy: string | null;
}

const VISIT_NOTE_SETTINGS_FIELDS = `
  noteType
  isProblemsEnabled
  isMedicationsEnabled
  isAllergiesEnabled
  isVitalsEnabled
  isCareGapsEnabled
  isPriorNotesEnabled
  updatedBy
`;

export const VISIT_NOTE_SETTINGS_QUERY = gql`
  query AcVisitNoteSettings {
    visitNoteSettings {
      ${VISIT_NOTE_SETTINGS_FIELDS}
    }
  }
`;

export interface VisitNoteSettingsResult {
  visitNoteSettings: VisitNoteSettings[];
}

export const SET_VISIT_NOTE_SETTINGS_MUTATION = gql`
  mutation AcSetVisitNoteSettings($input: VisitNoteSettingsInput!) {
    setVisitNoteSettings(input: $input) {
      ${VISIT_NOTE_SETTINGS_FIELDS}
    }
  }
`;

export interface SetVisitNoteSettingsResult {
  setVisitNoteSettings: VisitNoteSettings;
}

export interface SetVisitNoteSettingsVars {
  input: Omit<VisitNoteSettings, "updatedBy">;
}

// ── ROS / physical-exam templates ─────────────────────────────────────────────
//
// CLINICAL templates a physician edits at the point of care; this is where a
// practice changes what those dropdowns offer. Nothing here is generated.
//
// `@astrana/ui` ships the same defaults these rows are seeded with and falls
// back to them, so an empty or unreachable table costs a practice its
// customisation — never the exam itself.

const CLINICAL_TEMPLATE_FIELDS = `
  id
  axis
  systemKey
  label
  defaultFinding
  options
  sortOrder
  isActive
`;

export interface VisitNoteClinicalTemplateSystem {
  id: string;
  /** 'ros' | 'pe'. */
  axis: string;
  systemKey: string;
  label: string;
  defaultFinding: string;
  options: string[];
  sortOrder: number;
  isActive: boolean;
}

export const VISIT_NOTE_CLINICAL_TEMPLATES_QUERY = gql`
  query AcVisitNoteClinicalTemplates {
    visitNoteClinicalTemplates {
      ${CLINICAL_TEMPLATE_FIELDS}
    }
  }
`;

export interface VisitNoteClinicalTemplatesResult {
  visitNoteClinicalTemplates: VisitNoteClinicalTemplateSystem[];
}

export const SET_VISIT_NOTE_CLINICAL_TEMPLATE_MUTATION = gql`
  mutation AcSetVisitNoteClinicalTemplate(
    $input: VisitNoteClinicalTemplateInput!
  ) {
    setVisitNoteClinicalTemplate(input: $input) {
      ${CLINICAL_TEMPLATE_FIELDS}
    }
  }
`;

export interface SetVisitNoteClinicalTemplateResult {
  setVisitNoteClinicalTemplate: VisitNoteClinicalTemplateSystem;
}

export interface SetVisitNoteClinicalTemplateVars {
  input: Omit<VisitNoteClinicalTemplateSystem, "axis" | "systemKey">;
}

/**
 * Model aliases the SERVER accepts (config `azure.openai.deployments`). The model
 * picker is populated from this rather than a hardcoded list, so the console can
 * never offer an alias that would silently fall back to the default deployment.
 */
export const AI_MODEL_ALIASES_QUERY = gql`
  query AcAiModelAliases {
    aiModelAliases
  }
`;

export interface AiModelAliasesResult {
  aiModelAliases: string[];
}

/**
 * ai_template.key for each LEGACY note type — must match the backend's
 * `visitNotePurposeFor`.
 *
 * LEGACY, and still live: care-management-gql resolves these four keys for any
 * request that passes the deprecated `noteType` instead of a canonical
 * (format, standard) shape. They are NOT the keys the default path uses — see
 * CANONICAL_VISIT_NOTE_SHAPES below.
 */
export const VISIT_NOTE_TEMPLATE_KEYS: Record<VisitNoteType, string> = {
  SOAP: "visit-note.soap",
  SIMPLE: "visit-note.simple",
  EM: "visit-note.em",
  MEAT: "visit-note.meat",
};

export const VISIT_NOTE_TYPE_LABELS: Record<VisitNoteType, string> = {
  SOAP: "SOAP",
  SIMPLE: "Simple Visit",
  EM: "E&M",
  MEAT: "MEAT",
};

export const ALL_VISIT_NOTE_TYPES: VisitNoteType[] = [
  "SOAP",
  "SIMPLE",
  "EM",
  "MEAT",
];

// ── Canonical visit-note prompts (the DEFAULT path) ───────────────────────────
//
// care-management-gql models a visit note on two orthogonal axes — FORMAT
// (SOAP | HP | QUICK) and STANDARD (NONE | MEAT | EM) — and resolves the prompt
// for a canonical request under a NAMESPACED purpose:
//
//     visit-note.canonical.<format>[.<standard>]
//
// (`visitNotePurposeForShape` in
// src/services/astraconnect/visitNote/noteFormats.ts.) The canonical path is now
// the default, so these are the prompts actually in use — the four legacy keys
// above only serve callers that still pass `noteType`.
//
// Mirroring these keys here is what makes them ADMINISTRABLE. While the console
// knew only the legacy four, an admin could edit the "SOAP" prompt in a UI that
// looked authoritative and change nothing the server would ever send.
//
// ALL NINE PAIRS, not five. Both axes are free on the GraphQL input, so every
// combination is reachable, and any pair the console omits is a prompt silently
// stuck on the server's built-in fallback with no way to see or tune it.

export type NoteFormat = "SOAP" | "HP" | "QUICK";
export type DocumentationStandard = "NONE" | "MEAT" | "EM";

export interface CanonicalNoteShape {
  format: NoteFormat;
  standard: DocumentationStandard;
}

export const ALL_NOTE_FORMATS: NoteFormat[] = ["SOAP", "HP", "QUICK"];

export const ALL_DOCUMENTATION_STANDARDS: DocumentationStandard[] = [
  "NONE",
  "MEAT",
  "EM",
];

export const NOTE_FORMAT_LABELS: Record<NoteFormat, string> = {
  SOAP: "SOAP",
  HP: "H&P",
  QUICK: "Quick note",
};

export const DOCUMENTATION_STANDARD_LABELS: Record<
  DocumentationStandard,
  string
> = {
  NONE: "No documentation standard",
  MEAT: "MEAT",
  EM: "E&M",
};

/**
 * `ai_template.key` for a canonical shape.
 *
 * MUST stay in step with `visitNotePurposeForShape` in care-management-gql: a
 * mismatch does not error, it just binds the card to a key nothing resolves, so
 * the admin edits a template the server never reads. sql/0019 seeds exactly these
 * keys and canonicalVisitNoteKeys.test.ts pins the two together.
 */
export function canonicalVisitNoteTemplateKey({
  format,
  standard,
}: CanonicalNoteShape): string {
  const suffix = standard === "NONE" ? "" : `.${standard.toLowerCase()}`;
  return `visit-note.canonical.${format.toLowerCase()}${suffix}`;
}

/** Every reachable (format, standard) pair, format-major. */
export const CANONICAL_VISIT_NOTE_SHAPES: CanonicalNoteShape[] =
  ALL_NOTE_FORMATS.flatMap((format) =>
    ALL_DOCUMENTATION_STANDARDS.map((standard) => ({ format, standard })),
  );

/**
 * The legacy `visit_note_settings` row a canonical shape's data-source gate
 * resolves to — `settingsNoteTypeFor` in care-management-gql. Returns null for
 * H&P, which owns its own 'hp' row (seeded by sql/0018) that the admin API does
 * NOT yet expose: `VisitNoteSettings.noteType` is still the four-member legacy
 * enum, so no card can edit it and H&P runs with every source on. Stated in the
 * UI rather than hidden, because guessing wrong here is how an admin concludes a
 * toggle they cannot find is a toggle that does not exist.
 */
export function settingsNoteTypeForCanonicalShape({
  format,
  standard,
}: CanonicalNoteShape): VisitNoteType | null {
  if (format === "HP") return null;
  if (standard === "MEAT") return "MEAT";
  if (standard === "EM") return "EM";
  if (format === "QUICK") return "SIMPLE";
  return "SOAP";
}
